/* eslint-disable @typescript-eslint/member-ordering */
import { ComponentStore } from '@ngrx/component-store';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, from, EMPTY } from 'rxjs';
import { mergeMap, tap, switchMap, catchError } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { FindingParameters, Finding, StepTask } from '@telespot/domain';
import { FindingService, FindingChange } from '@analysis/domain';
import { type FindingRepository, SyncReviewedFindingChangesUseCase, SyncFindingsUseCase } from '@analysis/use-cases';
import { v4 } from 'uuid';

export interface FindingState {
  findings: EntityState<FindingParameters>;
  metadata: Map<string, string | string[]>;
  changedFindings: Map<string, FindingChange>;
  loading: boolean;
  error: string | null;
  syncingChanges: boolean;
}

export const FINDING_SERVICE = new InjectionToken<FindingRepository>('FINDING_SERVICE');
export const SYNC_REVIEWED_FINDING_CHANGES_USE_CASE = new InjectionToken<SyncReviewedFindingChangesUseCase>(
  'SYNC_REVIEWED_FINDING_CHANGES_USE_CASE'
);
export const SYNC_FINDINGS_USE_CASE = new InjectionToken<SyncFindingsUseCase>('SYNC_FINDINGS_USE_CASE');

@Injectable()
export class FindingStore extends ComponentStore<FindingState> {
  public readonly adapter: EntityAdapter<FindingParameters>;

  constructor(
    @Inject(FINDING_SERVICE) private readonly api: FindingRepository,
    @Inject(SYNC_REVIEWED_FINDING_CHANGES_USE_CASE)
    private readonly reviewFindingsUseCase: SyncReviewedFindingChangesUseCase,
    @Inject(SYNC_FINDINGS_USE_CASE)
    private readonly syncFindingsUseCase: SyncFindingsUseCase
  ) {
    const adapter = createEntityAdapter<FindingParameters>({
      selectId: (finding) => finding.uuid,
    });

    super({
      findings: adapter.getInitialState(),
      metadata: new Map(),
      changedFindings: new Map(),
      loading: false,
      error: null,
      syncingChanges: false,
    });

    this.adapter = adapter;
  }

  // Helper to get finding key (always uuid for indexing)
  private getFindingKey(finding: FindingParameters): string {
    return finding.uuid;
  }

  private toFindingParameters(finding: Finding): FindingParameters {
    return {
      id: finding.id,
      uuid: finding.uuid,
      version: finding.version,
      type: finding.type,
      analysisId: finding.analysisId,
      assetId: finding.assetId,
      creatorId: finding.creatorId,
      creatorEntity: finding.creatorEntity,
      pipelineStepId: finding.pipelineStepId,
      pipelineId: finding.pipelineId,
      originId: finding.originId,
      createdAt: finding.createdAt,
      updatedAt: finding.updatedAt,
      deletedAt: finding.deletedAt,
      data: finding.data,
    };
  }

  private prepareAssetFindings(
    state: FindingState,
    apiFindings: Finding[],
    assetId: string,
    analystId: string,
    analystEntity: FindingParameters['creatorEntity']
  ): FindingParameters[] {
    const entities = state.findings.entities;

    const belongsToTarget = (finding: FindingParameters) =>
      finding.assetId === assetId &&
      finding.creatorId === analystId &&
      finding.creatorEntity === analystEntity;

    const retained = Object.values(entities).filter((finding): finding is FindingParameters => {
      if (!finding) return false;
      if (!finding.assetId) return true;
      if (!belongsToTarget(finding)) return true;
      return state.changedFindings.has(finding.uuid);
    });

    const changedStepIds = new Set(
      retained.filter(belongsToTarget).map((finding) => finding.pipelineStepId)
    );

    const fromApi = apiFindings
      .map((finding) => this.toFindingParameters(finding))
      .filter((finding) => !changedStepIds.has(finding.pipelineStepId));

    return [...retained, ...fromApi];
  }

  private buildMetadataFromPrepared(
    state: FindingState,
    prepared: FindingParameters[]
  ): Map<string, string | string[]> {
    const metadata = new Map<string, string | string[]>();

    prepared.forEach((finding) => {
      if (state.changedFindings.has(finding.uuid)) {
        const localValue = state.metadata.get(finding.uuid);
        if (localValue !== undefined && localValue !== null) {
          metadata.set(finding.uuid, localValue as string | string[]);
        }
        return;
      }

      const extractedValue = FindingService.extractFindingValue(finding.data, finding.type);
      if (extractedValue !== null) {
        metadata.set(finding.uuid, extractedValue);
      }
    });

    return metadata;
  }

  // #region Selectors

  public readonly findings$ = this.select((state) => state.findings.entities);
  public readonly metadata$ = this.select((state) => state.metadata);
  public readonly loading$ = this.select((state) => state.loading);


  public readonly changedFindings$ = this.select((state) => state.changedFindings);
  public readonly unsynced$ = this.select((state) => state.changedFindings.size > 0);
  public readonly synced$ = this.select(this.unsynced$, (unsynced) => !unsynced);
  public readonly syncingChanges$ = this.select((state) => state.syncingChanges);

  // Type-specific selectors
  public readonly textFindings$ = this.select(this.findings$, (findings) =>
    Object.values(findings).filter((f) => f.type === StepTask.TEXT)
  );

  public readonly classificationFindings$ = this.select(this.findings$, (findings) =>
    Object.values(findings).filter((f) => f.type === StepTask.CLASSIFICATION)
  );

  public readonly nonTaggingFindings$ = this.select(this.findings$, (findings) =>
    Object.values(findings).filter((f) => f.type === StepTask.TEXT || f.type === StepTask.CLASSIFICATION)
  );

  public readonly findingsWithMetadata$ = this.select(this.findings$, this.metadata$, (findings, metadata) => {
    return Object.values(findings).map((finding) => ({
      id: finding.id,
      uuid: finding.uuid,
      version: finding.version,
      type: finding.type,
      analysisId: finding.analysisId,
      assetId: finding.assetId,
      creatorId: finding.creatorId,
      creatorEntity: finding.creatorEntity,
      pipelineStepId: finding.pipelineStepId,
      pipelineId: finding.pipelineId,
      originId: finding.originId,
      createdAt: finding.createdAt,
      updatedAt: finding.updatedAt,
      deletedAt: finding.deletedAt,
      metadata: metadata.get(finding.uuid) || null,
    }));
  });

  public readonly sampleFindings$ = this.select(this.findings$, (findings) =>
    Object.values(findings).filter((finding) => !finding.assetId)
  );

  public readonly sampleFindingsWithMetadata$ = this.select(this.findingsWithMetadata$, (findings) =>
    findings.filter((finding) => !finding.assetId)
  );

  public readonly newFindings$ = this.select(this.findings$, (findings) =>
    Object.values(findings).filter((finding) => finding.id === undefined)
  );

  // #endregion

  // #region Updaters

  public readonly setFindings = this.updater((state, findings: Finding[]) => {
    const findingParameters = findings.map((finding) => this.toFindingParameters(finding));
    const newMetadata = new Map(state.metadata);

    findingParameters.forEach((findingParams) => {
      const extractedValue = FindingService.extractFindingValue(findingParams.data, findingParams.type);
      newMetadata.set(findingParams.uuid, extractedValue);
    });

    return {
      ...state,
      findings: this.adapter.upsertMany(findingParameters, state.findings),
      metadata: newMetadata,
      loading: false,
      error: null,
    };
  });

  public readonly loadAssetFindingsResult = this.updater(
    (
      state,
      payload: {
        assetId: string;
        analystId: string;
        analystEntity: FindingParameters['creatorEntity'];
        apiFindings: Finding[];
      }
    ) => {
      const prepared = this.prepareAssetFindings(
        state,
        payload.apiFindings,
        payload.assetId,
        payload.analystId,
        payload.analystEntity
      );

      return {
        ...state,
        findings: this.adapter.setAll(prepared, state.findings),
        metadata: this.buildMetadataFromPrepared(state, prepared),
        loading: false,
        error: null,
      };
    }
  );

  public readonly removeFindings = this.updater((state, findingUuids: string[]) => {
    const newMetadata = new Map(state.metadata);

    // Remove metadata for deleted findings
    findingUuids.forEach((uuid) => {
      newMetadata.delete(uuid);
    });

    return {
      ...state,
      findings: this.adapter.removeMany(findingUuids, state.findings),
      metadata: newMetadata,
    };
  });

  public readonly updateFinding = this.updater(
    (
      state,
      payload: {
        findingId: string;
        data: string | string[];
        type: 'text' | 'classification';
      }
    ) => {
      const { findingId, data, type } = payload;

      const finding = state.findings.entities[findingId];

      if (!finding) return state;

      const newChangedFindings = new Map(state.changedFindings);
      const newMetadata = new Map(state.metadata);

      const existingChange = state.changedFindings.get(findingId);
      let originalData: string | string[] | null;

      if (existingChange) {
        originalData = existingChange.originalData;
      } else {
        const currentMetadata = state.metadata.get(findingId);
        if (currentMetadata !== undefined) {
          originalData = currentMetadata as string | string[];
        } else {
          originalData = null;
        }
      }

      let hasChanged = false;
      if (type === 'text') {
        hasChanged = originalData !== data;
      } else {
        hasChanged = JSON.stringify(originalData) !== JSON.stringify(data);
      }

      if (hasChanged) {
        newChangedFindings.set(findingId, {
          id: finding.id, // Store actual id (can be undefined for new findings)
          type,
          originalData,
          currentData: data,
          timestamp: Date.now(),
        });
      } else {
        newChangedFindings.delete(findingId);
      }

      newMetadata.set(findingId, data);

      return {
        ...state,
        metadata: newMetadata,
        changedFindings: newChangedFindings,
      };
    }
  );

  public readonly revertChanges = this.updater((state, findingUuids: string[]) => {
    const newChangedFindings = new Map(state.changedFindings);
    const newMetadata = new Map(state.metadata);

    findingUuids.forEach((findingUuid) => {
      const change = newChangedFindings.get(findingUuid);
      if (change) {
        newMetadata.set(findingUuid, change.originalData);
        newChangedFindings.delete(findingUuid);
      }
    });

    return {
      ...state,
      metadata: newMetadata,
      changedFindings: newChangedFindings,
    };
  });

  public readonly clearChanges = this.updater((state) => ({
    ...state,
    changedFindings: new Map(),
  }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
    ...(loading ? { error: null } : {}),
  }));

  public readonly setLoadError = this.updater((state, error: string) => ({
    ...state,
    error,
    loading: false,
  }));

  public readonly setSyncingChanges = this.updater((state, syncing: boolean) => ({
    ...state,
    syncingChanges: syncing,
    ...(syncing ? { error: null } : {}),
  }));

  public readonly setSyncError = this.updater((state, error: string) => ({
    ...state,
    error,
    syncingChanges: false,
  }));

  // Private updaters

  private readonly addFinding = this.updater(
    (state, payload: { finding: FindingParameters; metadata: string | string[] }) => {
      const { finding, metadata } = payload;
      const newMetadata = new Map(state.metadata);
      const newChangedFindings = new Map(state.changedFindings);
      const findingKey = this.getFindingKey(finding);

      newMetadata.set(findingKey, metadata);

      const findingParams: FindingParameters = {
        id: finding.id,
        uuid: finding.uuid,
        version: finding.version,
        type: finding.type,
        analysisId: finding.analysisId,
        assetId: finding.assetId,
        creatorId: finding.creatorId,
        creatorEntity: finding.creatorEntity,
        pipelineStepId: finding.pipelineStepId,
        pipelineId: finding.pipelineId,
        originId: finding.originId,
        createdAt: finding.createdAt || new Date(),
        updatedAt: finding.updatedAt || new Date(),
        deletedAt: finding.deletedAt || null,
        data: null,
      };

      if (finding.type === StepTask.TEXT || finding.type === StepTask.CLASSIFICATION) {
        newChangedFindings.set(findingKey, {
          id: finding.id, // Store actual id (can be undefined for new findings)
          type: finding.type as 'text' | 'classification',
          originalData: null,
          currentData: metadata,
          timestamp: Date.now(),
        });
      }

      return {
        ...state,
        findings: this.adapter.upsertOne(findingParams, state.findings),
        metadata: newMetadata,
        changedFindings: newChangedFindings,
      };
    }
  );

  private readonly removeFinding = this.updater((state, findingUuid: string) => {
    const newMetadata = new Map(state.metadata);
    newMetadata.delete(findingUuid);

    return {
      ...state,
      findings: this.adapter.removeOne(findingUuid, state.findings),
      metadata: newMetadata,
    };
  });

  // #endregion

  // #region Effects

  public readonly fetchSampleFindings = this.effect(
    (
      params$: Observable<{
        sampleId: string;
        pipelineStepIds: string[];
        analystId: string;
        analystEntity: 'algorithm' | 'user';
        limit?: number;
      }>
    ) =>
      params$.pipe(
        mergeMap(({ sampleId, pipelineStepIds, analystId, analystEntity, limit }) => {
          this.setLoading(true);

          return from(
            this.api.findSampleFindings({
              sampleId,
              pipelineStepIds,
              creatorId: analystId,
              creatorEntity: analystEntity,
              limit,
            })
          ).pipe(
            tap({
              next: (findings) => this.setFindings(findings),
              error: (error) => {
                this.setLoadError(error.message);
              },
              complete: () => {
                this.setLoading(false);
              },
            })
          );
        })
      )
  );

  public readonly fetchAssetFindings = this.effect(
    (
      action$: Observable<{
        assetId: string;
        sampleId: string;
        pipelineStepIds: string[];
        analystId: string;
        analystEntity: 'user' | 'algorithm';
      }>
    ) =>
      action$.pipe(
        switchMap((params) => {
          this.setLoading(true);

          return from(
            this.api.findFindingsForAsset({
              assetId: params.assetId,
              sampleId: params.sampleId,
              pipelineStepIds: params.pipelineStepIds,
              creatorId: params.analystId,
              creatorEntity: params.analystEntity,
            })
          ).pipe(
            tap({
              next: (findings) =>
                this.loadAssetFindingsResult({
                  assetId: params.assetId,
                  analystId: params.analystId,
                  analystEntity: params.analystEntity,
                  apiFindings: findings,
                }),
              error: (error) => {
                this.setLoadError(error.message);
              },
              complete: () => {
                this.setLoading(false);
              },
            })
          );
        })
      )
  );

  // #endregion

  // #region Public Methods

  // #endregion

  public readonly createFinding = this.effect(
    (
      action$: Observable<{
        assetId: string;
        creatorId: string;
        creatorEntity: 'user' | 'algorithm';
        pipelineId: string;
        taskId: string;
        taskType: StepTask;
        value: string | string[];
        existingFindings: FindingParameters[];
      }>
    ) =>
      action$.pipe(
        mergeMap((params) => {
          const { assetId, creatorId, creatorEntity, pipelineId, taskId, taskType, value, existingFindings } = params;

          const analysisId = existingFindings.find((finding) => finding.analysisId)?.analysisId;

          const finding = {
            id: undefined,
            assetId,
            creatorId,
            creatorEntity,
            analysisId,
            pipelineId,
            version: 1,
            pipelineStepId: taskId,
            data: null,
            type: taskType,
            uuid: v4(),
            originId: undefined,
            createdAt: new Date(),
            updatedAt: new Date(),
            deletedAt: undefined,
          };

          this.addFinding({ finding, metadata: value });

          return EMPTY;
        })
      )
  );

  public readonly syncChanges = this.effect((action$: Observable<{ sampleId: string; currentAnalystId: string }>) =>
    action$.pipe(
      concatLatestFrom(() => [this.findings$, this.changedFindings$]),
      switchMap(([action, findings, changes]) => {
        if (changes.size === 0) return EMPTY;
        this.setSyncingChanges(true);

        const findingsList = Object.values(findings);

        return from(
          this.syncFindingsUseCase.execute({
            sampleId: action.sampleId,
            findings: findingsList,
            changedFindings: changes,
          })
        ).pipe(
          tap((savedFindings) => {
            this.updateFindingsAfterSync(savedFindings);
          }),
          catchError((error) => {
            this.setSyncError(error.message);
            return EMPTY;
          })
        );
      })
    )
  );

  public readonly reviewAnalysis = this.effect(
    (action$: Observable<{ sampleId: string; sourceAnalystId: string; currentAnalystId: string }>) =>
      action$.pipe(
        concatLatestFrom(() => [this.changedFindings$, this.newFindings$]),
        switchMap(([{ sampleId, sourceAnalystId, currentAnalystId }, changedFindings, newFindings]) => {
          this.setSyncingChanges(true);

          return from(
            this.reviewFindingsUseCase.execute({
              sampleId,
              currentAnalystId,
              sourceAnalystId,
              changedFindings,
              newFindings,
            })
          ).pipe(
            tap(({ savedFindings }) => {
              this.updateFindingsAfterSync(savedFindings);
            }),
            catchError((error) => {
              this.setSyncError(error.message);
              return EMPTY;
            })
          );
        })
      )
  );

  public updateFindingsAfterSync(savedFindings: Finding[]): void {
    this.patchState((state) => {
      const newMetadata = new Map(state.metadata);

      const findingUuidsToRemove = savedFindings.map((finding) => finding.uuid);

      // Remove old findings by uuid (adapter uses uuid as key)
      findingUuidsToRemove.forEach((uuid) => {
        newMetadata.delete(uuid);
      });
      const findingsAfterRemoval = this.adapter.removeMany(findingUuidsToRemove, state.findings);

      const findingParameters: FindingParameters[] = [];
      savedFindings.forEach((finding) => {
        const findingParams: FindingParameters = {
          id: finding.id,
          uuid: finding.uuid,
          version: finding.version,
          type: finding.type,
          analysisId: finding.analysisId,
          assetId: finding.assetId,
          creatorId: finding.creatorId,
          creatorEntity: finding.creatorEntity,
          pipelineStepId: finding.pipelineStepId,
          pipelineId: finding.pipelineId,
          originId: finding.originId,
          createdAt: finding.createdAt,
          updatedAt: finding.updatedAt,
          deletedAt: finding.deletedAt,
          data: finding.data,
        };
        findingParameters.push(findingParams);

        const extractedValue = FindingService.extractFindingValue(finding.data, finding.type);
        newMetadata.set(finding.uuid, extractedValue);
      });
      const finalFindings = this.adapter.upsertMany(findingParameters, findingsAfterRemoval);

      return {
        ...state,
        findings: finalFindings,
        metadata: newMetadata,
        changedFindings: new Map(),
        syncingChanges: false,
        error: null,
      };
    });
  }
}
